import { PaginatedResponse } from "@/interfaces/paginatedResponse";
import useInternalSearchUser from "@/internal/modules/users/composables/useInternalSearchUser";
import axios from "@/lib/axios";
import useUsersSearch from "@/modules/users/composables/useUsersSearch";
import { User } from "@/modules/users/interfaces/user";
import { useRoleUsersStore } from "@/stores/roleUsers";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";
import { ref, watch } from "vue";
import { ToastInterface, useToast } from "vue-toastification";

interface Params {
  roleId: number;
  immediate?: boolean;
  page?: number;
}

const getRoleUsers = async (
  roleId: number,
  page: number,
): Promise<PaginatedResponse<User>> => {
  const { data } = await axios.get<PaginatedResponse<User>>(
    `api/internal/roles/${roleId}/users?page=${page}`,
  );

  return data;
};

const postRoleUsers = async (
  roleId: number,
  users: number[],
  toast: ToastInterface,
) => {
  try {
    const data = await axios.post(`api/internal/roles/${roleId}/users`, {
      users,
    });

    toast.success("Usuarios asignados actualizados correctamente");
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const useInternalRoleUsers = (props: Params) => {
  const { immediate = true } = props;
  const toast = useToast();

  const page = ref(1);
  const { roleId, roleUsers } = storeToRefs(useRoleUsersStore());
  const { setRoleUsers } = useRoleUsersStore();

  const setPage = (newPage: number) => {
    page.value = newPage;
  };

  const {
    data,
    error,
    isLoading,
    refetch: refetchUsers,
  } = useQuery({
    queryKey: ["roleUsers", props.roleId],
    queryFn: () => getRoleUsers(props.roleId, page.value),
  });

  const roleUsersMutation = useMutation((users: number[]) =>
    postRoleUsers(props.roleId, users, toast),
  );

  const {
    data: usersInternal,
    isLoading: loadingUsersInternal,
    refetch,
    setQuery: setQueryInternal,
  } = useInternalSearchUser({
    immediate: true,
    params: {
      internalOnly: 1,
    },
  });

  watch(
    data,
    (value) => {
      if (value) {
        setRoleUsers(value.data);
      }
    },
    {
      immediate,
    },
  );

  watch(page, (value) => {
    if (value) {
      refetch();
    }
  });

  return {
    data,
    roleUsers,
    error,
    isLoading,
    page,
    setPage,

    users: usersInternal,
    loadingUsers: loadingUsersInternal,
    setQuery: setQueryInternal,
    searchUsers: refetch,

    refetchUsers,

    roleUsersMutation,
    roleUsersMutationLoading: roleUsersMutation.isLoading,
    roleUsersMutationAsync: roleUsersMutation.mutateAsync,
  };
};

export default useInternalRoleUsers;
